import { Component, Input, Output, EventEmitter, ViewChild } from '@angular/core';

import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule, MatMenu } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';

export interface CallCenterChipConfig {
  text: string;
  type: CallCenterChipType;
  validStatus?: boolean | null; // true = valid (green), false = invalid (red), null = neutral (gray)
  showMenu?: boolean;
  menuActions?: ChipMenuAction[];
}

export type CallCenterChipType = 'status' | 'official' | 'phone' | 'email' | 'contract-status' | 'user';

export interface ChipMenuAction {
  label: string;
  icon: string;
  action: 'copy' | 'add' | 'edit' | 'delete';
}

@Component({
  selector: 'app-call-center-chip',
  standalone: true,
  imports: [MatChipsModule, MatIconModule, MatMenuModule, MatButtonModule],
  template: `
    <mat-chip
      [class]="getChipClass()"
      [matMenuTriggerFor]="config.showMenu ? chipMenu : null"
      [disabled]="config.type === 'user'">
      {{ config.text }}
      @if (config.showMenu) {
        <mat-icon
          matChipTrailingIcon
          class="menu-icon">
          more_vert
        </mat-icon>
      }
    </mat-chip>
    
    <!-- Chip Menu -->
    @if (config.showMenu && config.menuActions) {
      <mat-menu #chipMenu="matMenu">
        @for (action of config.menuActions; track action) {
          <button
            mat-menu-item
            (click)="onMenuAction(action.action)">
            <mat-icon>{{ action.icon }}</mat-icon>
            <span>{{ action.label }}</span>
          </button>
        }
      </mat-menu>
    }
    `,
  styleUrls: ['./call-center-chip.component.scss']
})
export class CallCenterChipComponent {
  @Input() config!: CallCenterChipConfig;
  @Output() menuActionClicked = new EventEmitter<{ action: string; chipText: string }>();
  @ViewChild('chipMenu') chipMenu!: MatMenu;

  // Static helper methods for creating chip configs
  static createStatusChip(status: string, getStatusLabel: (status: string) => string): CallCenterChipConfig {
    return {
      text: getStatusLabel(status),
      type: 'status'
    };
  }

  static createOfficialChips(officials: string[], validStatuses?: { [key: string]: boolean | null }): CallCenterChipConfig[] {
    return officials.map(official => ({
      text: official,
      type: 'official',
      validStatus: validStatuses?.[official] ?? null
    }));
  }

  static createPhoneChips(phones: string[], validStatuses?: { [key: string]: boolean | null }): CallCenterChipConfig[] {
    return phones.map(phone => ({
      text: phone,
      type: 'phone',
      validStatus: validStatuses?.[phone] ?? null
    }));
  }

  static createEmailChips(emails: string[], validStatuses?: { [key: string]: boolean | null }): CallCenterChipConfig[] {
    return emails.map(email => ({
      text: email,
      type: 'email',
      validStatus: validStatuses?.[email] ?? null
    }));
  }

  static createContractStatusChip(status: string, showMenu: boolean = false, menuActions?: ChipMenuAction[]): CallCenterChipConfig {
    return {
      text: status,
      type: 'contract-status',
      showMenu: showMenu,
      menuActions: menuActions
    };
  }

  static createContractStatusChips(statuses: string[], showMenu: boolean = false, menuActions?: ChipMenuAction[]): CallCenterChipConfig[] {
    return statuses.map(status => ({
      text: status,
      type: 'contract-status',
      showMenu: showMenu,
      menuActions: menuActions
    }));
  }

  static createUserChip(userName: string): CallCenterChipConfig {
    return {
      text: userName,
      type: 'user',
      showMenu: false // User chipek nem kattinthatóak és nincs menü
    };
  }

  static createUserChips(userNames: string[]): CallCenterChipConfig[] {
    return userNames.map(userName => ({
      text: userName,
      type: 'user',
      showMenu: false // User chipek nem kattinthatóak és nincs menü
    }));
  }

  getChipClass(): string {
    const baseClasses = ['call-center-chip'];
    
    if (this.config.type === 'status') {
      // Státusz chipek esetében speciális színezés
      baseClasses.push(this.getStatusClass(this.config.text));
      baseClasses.push('clickable-chip'); // Status chips are always clickable
      
      // ValidStatus alapú színezés státusz chipeknél is (kijelölés megjelenítéséhez)
      if (this.config.validStatus === true) {
        baseClasses.push('chip-selected'); // Kijelölt státusz

      }
    } else if (this.config.type === 'contract-status') {
      // Contract státusz chipek esetében speciális színezés
      baseClasses.push(this.getContractStatusClass(this.config.text));
      baseClasses.push('clickable-chip'); // Contract status chips are always clickable
      
      // ValidStatus alapú színezés contract státusz chipeknél is (kijelölés megjelenítéséhez)
      if (this.config.validStatus === true) {
        baseClasses.push('chip-selected'); // Kijelölt contract státusz
      }
    } else if (this.config.type === 'user') {
      // User chipek esetében speciális színezés
      baseClasses.push(this.getUserClass(this.config.text));
      // User chipek nem kattinthatóak
    } else {
      // Kontakt chipek esetében validstatus alapján színezés
      baseClasses.push('contact-chip');
      baseClasses.push('clickable-chip');
      

      
      if (this.config.validStatus === true) {
        baseClasses.push('chip-valid');
      } else if (this.config.validStatus === false) {
        baseClasses.push('chip-invalid');
      } else if (this.config.validStatus === null) {
        baseClasses.push('chip-neutral');
      } else {
        // Ha nincs validstatus megadva (undefined), akkor típus alapján színezés
        baseClasses.push(`${this.config.type}-chip`); // official-chip, phone-chip, email-chip
      }
    }

    const result = baseClasses.join(' ');
    return result;
  }

  private getStatusClass(status: string): string {
    const normalizedStatus = status.toLowerCase()
      .replace(/á/g, 'a')
      .replace(/é/g, 'e')
      .replace(/í/g, 'i')
      .replace(/ó/g, 'o')
      .replace(/ö/g, 'o')
      .replace(/ő/g, 'o')
      .replace(/ú/g, 'u')
      .replace(/ü/g, 'u')
      .replace(/ű/g, 'u')
      .replace(/\s+/g, '-')  // Szóközöket kötőjellre cseréli
      .replace(/[()]/g, '')  // Zárójeleket eltávolítja
      .replace(/\+/g, '');   // + jelet eltávolítja
    
    return `status-${normalizedStatus}`;
  }

  private getContractStatusClass(status: string): string {
    const normalizedStatus = status.toLowerCase()
      .replace(/á/g, 'a')
      .replace(/é/g, 'e')
      .replace(/í/g, 'i')
      .replace(/ó/g, 'o')
      .replace(/ö/g, 'o')
      .replace(/ő/g, 'o')
      .replace(/ú/g, 'u')
      .replace(/ü/g, 'u')
      .replace(/ű/g, 'u')
      .replace(/\s+/g, '-')  // Szóközöket kötőjellre cseréli
      .replace(/,\s*/g, '-') // Vesszőt és utána lévő szóközöket kötőjellre cseréli
      .replace(/[()]/g, ''); // Zárójeleket eltávolítja
    
    return `contract-status-${normalizedStatus}`;
  }

  private getUserClass(userName: string): string {
    const normalizedName = userName.toLowerCase()
      .replace(/á/g, 'a')
      .replace(/é/g, 'e')
      .replace(/í/g, 'i')
      .replace(/ó/g, 'o')
      .replace(/ö/g, 'o')
      .replace(/ő/g, 'o')
      .replace(/ú/g, 'u')
      .replace(/ü/g, 'u')
      .replace(/ű/g, 'u')
      .replace(/\s+/g, '-')  // Szóközöket kötőjellre cseréli
      .replace(/[()]/g, ''); // Zárójeleket eltávolítja
    
    return `user-${normalizedName}`;
  }

  onMenuAction(action: string) {
    this.menuActionClicked.emit({
      action: action,
      chipText: this.config.text
    });
  }
}